import React, { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material'
import dayjs from 'dayjs'
import html2pdf from 'html2pdf.js'
import { chatApi, agentsApi } from '../providers/env'
import { useNotify } from 'react-admin'
import { chatPricingSettingsApi } from '../providers/env'

function getRFC3339(date: string) {
  const d = new Date(date)
  return d.toISOString()
}

export const getPricing = async (organizationId?: string | null) => {
  try {
    console.log("getPricing called with organizationId:", organizationId)
    let response: any
    
    if (organizationId) {
      response = await chatPricingSettingsApi.getAllChatPricingSettings({
        organizationId: organizationId
      })
      console.log("Called API with organizationId:", organizationId)
    } else {
      response = await chatPricingSettingsApi.getAllChatPricingSettings()
      console.log("Called API without organizationId")
    }
    
    let pricingList: any[] = []
    
    console.log("Raw API response:", response)
    console.log("response.data:", response?.data)
    console.log("Is response.data an array?", Array.isArray(response?.data))
    
    if (response?.data !== undefined) {
      pricingList = Array.isArray(response.data) ? response.data : [response.data]
      console.log("Extracted from response.data, count:", pricingList.length)
    } else if (Array.isArray(response)) {
      pricingList = response
      console.log("Using response as array, count:", pricingList.length)
    } else if (response) {
      pricingList = [response]
      console.log("Using response as single object")
    }
    
    console.log("Final pricingList:", pricingList)
    
    if (pricingList.length === 0) {
      console.warn("No pricing found - pricingList is empty")
      return null
    }
    
    const pricing = pricingList[0]
    console.log("Selected pricing object:", pricing)
    console.log("Pricing slabs:", pricing?.pricing_slabs)
    console.log("Pricing slabs type:", typeof pricing?.pricing_slabs)
    console.log("Pricing slabs is array?", Array.isArray(pricing?.pricing_slabs))
    
    if (!pricing.pricing_slabs || !Array.isArray(pricing.pricing_slabs)) {
      console.warn("pricing_slabs is missing or not an array, setting to empty")
      pricing.pricing_slabs = []
    } else {
      console.log("✅ Pricing slabs found:", pricing.pricing_slabs.length, "slabs")
    }
    
    return pricing
  } catch (error: any) {
    console.error("Error fetching pricing:", error)
    if (error?.response) {
      console.error("Error response:", error.response.data)
      console.error("Error status:", error.response.status)
    }
    return null
  }
}

const calculateChatCost = (chatCount: number, pricingSlabs: any[]): { rate: number; amount: number } => {
  if (chatCount <= 0 || !pricingSlabs || pricingSlabs.length === 0) {
    return { rate: 0, amount: 0 }
  }

  for (const slab of pricingSlabs) {
    const minChats = slab.min_chats || 0
    const maxChats = slab.max_chats
    const costPerChat = slab.cost_per_chat || 0

    if (maxChats === null || maxChats === undefined) {
      if (chatCount >= minChats) {
        return { rate: costPerChat, amount: chatCount * costPerChat }
      }
    } else {
      if (chatCount >= minChats && chatCount <= maxChats) {
        return { rate: costPerChat, amount: chatCount * costPerChat }
      }
    }
  }

  return { rate: 0, amount: 0 }
}

const BillingPage = () => {
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [pricing, setPricing] = useState<any>(null)
  const [chatCount, setChatCount] = useState<number>(0)
  const [chatAmount, setChatAmount] = useState<number>(0)
  const [slabRate, setSlabRate] = useState<number>(0)
  const [showInvoice, setShowInvoice] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null)
  const [currentAgentName, setCurrentAgentName] = useState<string>('')
  const [userOrganizationId, setUserOrganizationId] = useState<string | null>(null)
  const notify = useNotify()

  useEffect(() => {
    const loadUserOrganization = () => {
      try {
        const permissions = localStorage.getItem("permissions")
        if (permissions) {
          const userInfo = JSON.parse(permissions)
          const orgId = userInfo.organization_id || null
          setUserOrganizationId(orgId)
          return orgId
        }
      } catch (error) {
        console.error("Error loading user organization", error)
      }
      return null
    }

    // Get current agent ID from localStorage
    const agentId = localStorage.getItem("agentId")
    if (!agentId) {
      setError('No agent selected. Please select an agent first.')
      return
    }
    setCurrentAgentId(agentId)

    // Load agent details to get the name
    agentsApi.getAgents()
      .then((res) => {
        const allAgents = res.data || []
        const currentAgent = allAgents.find((a) => a.id === agentId)
        if (currentAgent) {
          setCurrentAgentName(currentAgent.name)
        } else {
          setError('Agent not found. Please select a valid agent.')
        }
      })
      .catch(() => notify("Error fetching agent details", { type: "error" }))

    const orgId = loadUserOrganization()
    getPricing(orgId).then(setPricing).catch(() => notify("Error fetching pricing", { type: "error" }))
  }, [notify])

  const handleDateChange = (value: string, setter: (val: string) => void) => {
    const parts = value.split('-')
    if (parts[0] && parts[0].length > 4) {
      parts[0] = parts[0].slice(0, 4)
      value = parts.join('-')
    }
    setter(value)
  }

  const validateDateRange = () => {
    if (fromDate && toDate) {
      const from = new Date(fromDate)
      const to = new Date(toDate)
      if (to <= from) {
        setError('To date must be greater than from date.')
        return false
      }
    }
    setError(null)
    return true
  }

  const handleFilter = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setShowInvoice(false)

    if (!fromDate || !toDate) {
      setError('Please select both dates.')
      return
    }

    if (!validateDateRange()) {
      return
    }

    setLoading(true)
    try {
      if (!userOrganizationId) {
        setError('User organization not found. Cannot generate invoice.')
        setLoading(false)
        return
      }

      if (!currentAgentId) {
        setError('No agent selected. Please select an agent first.')
        setLoading(false)
        return
      }

      const requestParams: any = {
        startDate: getRFC3339(fromDate),
        endDate: getRFC3339(toDate),
        organizationId: userOrganizationId,
        agentId: currentAgentId
      }

      console.log('Fetching chat count - requestParams:', requestParams)
      const response = await chatApi.getChatsCount(requestParams)
      
      const count = response.data.total_count || 0
      console.log(`Chat count for agent ${currentAgentId}:`, count)
      setChatCount(count)

      if (!pricing || !pricing.pricing_slabs || pricing.pricing_slabs.length === 0) {
        setError('Pricing information not available. Please try again.')
        setLoading(false)
        return
      }

      const { rate, amount } = calculateChatCost(count, pricing.pricing_slabs || [])
      
      setSlabRate(rate)
      setChatAmount(amount)
      setShowInvoice(true)
    } catch (err) {
      console.error('Error fetching chat count:', err)
      setError('Failed to fetch chat count. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle clear
  const handleClear = () => {
    setFromDate('')
    setToDate('')
    setShowInvoice(false)
    setError(null)
    setChatCount(0)
    setChatAmount(0)
    setSlabRate(0)
  }

  // Download invoice as PDF
  const handleDownload = () => {
    const element = document.getElementById('invoiceContent')
    if (!element) return
    html2pdf().from(element).save('Invoice.pdf')
  }

  // Random invoice number
  const invoiceNo = `AISE-${dayjs().format('YYYYMMDD-HHmmss')}`

  // Format date for display (MM/DD/YY)
  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return ''
    return dayjs(dateStr).format('MM/DD/YY')
  }


  return (
    <Box className="main-wrapper" sx={{ p: 2 }}>
      <Box className="page-wrapper">
        <Box className="content container-fluid">
          {/* Filter Section */}
          <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filter Section
              </Typography>
              <form onSubmit={handleFilter}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="From Date"
                      type="date"
                      fullWidth
                      value={fromDate}
                      onChange={(e) => handleDateChange(e.target.value, setFromDate)}
                      onBlur={validateDateRange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: '9999-12-31' }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="To Date"
                      type="date"
                      fullWidth
                      value={toDate}
                      onChange={(e) => handleDateChange(e.target.value, setToDate)}
                      onBlur={validateDateRange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: '9999-12-31' }}
                      required
                    />
                  </Grid>
                  {currentAgentName && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        <strong>Agent:</strong> {currentAgentName}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
                <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? 'Loading...' : 'Apply Filter'}
                  </Button>
                  <Button type="button" variant="outlined" onClick={handleClear}>
                    Clear
                  </Button>
                </Box>
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
              </form>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Packages Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Packages
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Subscription Type</TableCell>
                          <TableCell>Cost (USD)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Subscription Cost</TableCell>
                          <TableCell>
                            {pricing && pricing.subscription_cost ? pricing.subscription_cost.toFixed(3) : '-'}
                          </TableCell>
                        </TableRow>
                        {pricing && pricing.pricing_slabs && pricing.pricing_slabs.map((slab: any, index: number) => {
                          const rangeLabel = slab.max_chats 
                            ? `${slab.min_chats.toLocaleString()}-${slab.max_chats.toLocaleString()} chats`
                            : `${slab.min_chats.toLocaleString()}+ chats`;
                          return (
                            <TableRow key={index}>
                              <TableCell>{rangeLabel}</TableCell>
                              <TableCell>{slab.cost_per_chat ? slab.cost_per_chat.toFixed(3) : '0.000'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Invoice Card */}
            <Grid item xs={12} md={8}>
              {showInvoice && (
                <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                  <CardContent>
                    <Box id="invoiceContent">
                      {/* Header */}
                      <Box
                        sx={{
                          background: '#4a90e2',
                          color: 'white',
                          p: 2,
                          borderRadius: '8px 8px 0 0',
                        }}
                      >
                        <Grid container alignItems="center">
                          <Grid item xs={12}>
                            <Typography variant="h5">AISE</Typography>
                            <Typography variant="body2">www.aivo.asia</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      {/* Invoice Details */}
                      <Box
                        sx={{
                          borderBottom: '1px solid #ddd',
                          p: { xs: 2, md: 3 },
                        }}
                      >
                        <Grid container>
                          <Grid item xs={12} md={6}>
                            <Typography>
                              <strong>Invoice No:</strong> {invoiceNo}
                            </Typography>
                            <Typography>
                              <strong>Date:</strong> {dayjs().format('MM/DD/YY')}
                            </Typography>
                            <Typography>
                              <strong>Agent:</strong> {currentAgentName || 'Unknown agent'}
                            </Typography>
                            <Typography>
                              <strong>From Date:</strong> {formatDateForDisplay(fromDate)}
                            </Typography>
                            <Typography>
                              <strong>To Date:</strong> {formatDateForDisplay(toDate)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6} textAlign={{ md: 'right', xs: 'left' }}>
                            <Typography>8 Changi South Lane, #03-01</Typography>
                            <Typography>Singapore 486113</Typography>
                            <Typography>Email: cedric@aivo.asia</Typography>
                            <Typography>Phone: +65 9232 4242</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      {/* Invoice Table */}
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell align="center">Quantity</TableCell>
                                <TableCell align="center">Rate (USD)</TableCell>
                                <TableCell align="right">Amount (USD)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>Chat Usage</TableCell>
                                <TableCell align="center">{chatCount}</TableCell>
                                <TableCell align="center">{(slabRate || 0).toFixed(3)}</TableCell>
                                <TableCell align="right">{(chatAmount || 0).toFixed(3)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Subscription Fee</TableCell>
                                <TableCell align="center">1</TableCell>
                                <TableCell align="center">
                                  {pricing && pricing.subscription_cost ? pricing.subscription_cost.toFixed(3) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {pricing && pricing.subscription_cost ? pricing.subscription_cost.toFixed(3) : '-'}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={3} align="right">
                                  <strong>Subtotal</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>
                                    {(chatAmount + (pricing?.subscription_cost || 0)).toFixed(3)}
                                  </strong>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                            <TableHead>
                              <TableRow>
                                <TableCell colSpan={3} align="right">
                                  <strong>Total</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>
                                    {(chatAmount + (pricing?.subscription_cost || 0)).toFixed(3)}
                                  </strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                          </Table>
                        </TableContainer>
                      </Box>
                      {/* Footer */}
                      <Box
                        sx={{
                          background: '#f5f5f5',
                          p: { xs: 2, md: 3 },
                          textAlign: 'center',
                        }}
                      >
                        <Typography fontStyle="italic">
                          Thank you for using our services! We truly appreciate your trust and
                          support.
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button variant="contained" onClick={handleDownload}>
                        Download Invoice
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

export default BillingPage